import React, { useState, useEffect, useCallback } from 'react';
import { 
  Shield, AlertTriangle, CheckCircle, XCircle, Search, 
  RefreshCw, BarChart3, Clock, Filter, Send, Eye,
  ShieldAlert, ShieldCheck, Activity, FileText, TrendingUp
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Switch } from '../../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { useAdminSettings } from '../../../hooks/useAdminSettings';
import ModerationCharts from './ModerationCharts';

const ModerationTab = () => {
  const { apiCall, showSuccess, showError } = useAdminSettings();
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [testText, setTestText] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [logFilter, setLogFilter] = useState('all');

  const fetchSettings = useCallback(async () => {
    try {
      const data = await apiCall('get', '/api/moderation/settings');
      setSettings(data);
    } catch (error) {
      console.error('Error fetching moderation settings:', error);
    }
  }, [apiCall]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await apiCall('get', '/api/moderation/stats?days=30');
      setStats(data);
    } catch (error) {
      console.error('Error fetching moderation stats:', error);
    }
  }, [apiCall]);

  const fetchLogs = useCallback(async () => {
    try {
      const data = await apiCall('get', `/api/moderation/logs?limit=50`);
      setLogs(data.logs || []);
    } catch (error) {
      console.error('Error fetching moderation logs:', error);
    }
  }, [apiCall]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchSettings(), fetchStats(), fetchLogs()]);
      setLoading(false);
    };
    loadData();
  }, [fetchSettings, fetchStats, fetchLogs]);

  const handleTestModeration = async () => {
    if (!testText.trim()) {
      showError('Error', 'Please enter text to test');
      return;
    }
    
    setTesting(true);
    setTestResult(null);
    
    try {
      const data = await apiCall('post', `/api/moderation/test?text=${encodeURIComponent(testText)}`);
      setTestResult(data);
    } catch (error) {
      showError('Error', 'Failed to test moderation');
    } finally {
      setTesting(false);
    }
  };

  const handleToggleSetting = async (key, value) => {
    try {
      const updatedSettings = { ...settings, [key]: value };
      await apiCall('put', '/api/moderation/settings', updatedSettings);
      setSettings(updatedSettings);
      showSuccess('Settings Updated', `${key.replace(/_/g, ' ')} is now ${value ? 'enabled' : 'disabled'}`);
    } catch (error) {
      showError('Error', 'Failed to update settings');
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      self_harm: '🩹',
      racism: '🚫',
      child_abuse: '⚠️',
      animal_abuse: '🐾',
      copyright_infringement: '©️',
      terrorism: '💥',
      drug_promotion: '💊',
      spam: '📧',
      scam: '🎣',
      impersonation: '🎭'
    };
    return icons[category.toLowerCase()] || '⚡';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 animate-spin text-yellow-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={Activity}
          title="Total Moderated"
          value={stats?.total_moderated || 0}
          color="text-blue-400"
          bgColor="from-blue-500/20 to-cyan-500/20"
        />
        <StatCard
          icon={ShieldCheck}
          title="Approved"
          value={stats?.approved || 0}
          color="text-green-400"
          bgColor="from-green-500/20 to-emerald-500/20"
        />
        <StatCard
          icon={ShieldAlert}
          title="Rejected"
          value={stats?.rejected || 0}
          color="text-red-400"
          bgColor="from-red-500/20 to-rose-500/20"
        />
        <StatCard
          icon={Eye}
          title="Pending Review"
          value={stats?.flagged_for_review || 0}
          color="text-yellow-400"
          bgColor="from-yellow-500/20 to-orange-500/20"
        />
      </div>

      <Tabs defaultValue="charts" className="space-y-4">
        <TabsList className="bg-gray-800/50">
          <TabsTrigger value="charts" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
            <TrendingUp className="w-4 h-4 mr-2" />
            Trends
          </TabsTrigger>
          <TabsTrigger value="test" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
            <Search className="w-4 h-4 mr-2" />
            Test Moderation
          </TabsTrigger>
          <TabsTrigger value="logs" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
            <FileText className="w-4 h-4 mr-2" />
            Logs
          </TabsTrigger>
          <TabsTrigger value="settings" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
            <Shield className="w-4 h-4 mr-2" />
            Settings
          </TabsTrigger>
          <TabsTrigger value="categories" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
            <BarChart3 className="w-4 h-4 mr-2" />
            Categories
          </TabsTrigger>
        </TabsList>

        {/* Charts Tab - Trend Visualizations */}
        <TabsContent value="charts">
          <ModerationCharts />
        </TabsContent>

        {/* Test Moderation Tab */}
        <TabsContent value="test">
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Search className="w-5 h-5 text-yellow-400" />
                Test Content Moderation
              </CardTitle>
              <CardDescription className="text-gray-400">
                Enter text to test the AI moderation system. This helps verify the system is working correctly.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm text-gray-400">Test Content</label>
                <textarea
                  value={testText}
                  onChange={(e) => setTestText(e.target.value)}
                  placeholder="Enter text to test moderation (e.g., title, description, or any content)..."
                  className="w-full h-32 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 resize-none focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={handleTestModeration}
                  disabled={testing || !testText.trim()}
                  className="bg-yellow-500 hover:bg-yellow-600 text-black"
                >
                  {testing ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Test Moderation
                </Button>
                
                <Button
                  onClick={() => { setTestText(''); setTestResult(null); }}
                  variant="outline"
                  className="border-gray-600"
                >
                  Clear
                </Button>
              </div>

              {/* Test Result */}
              {testResult && (
                <div className={`mt-6 p-4 rounded-lg border ${
                  testResult.passed 
                    ? 'bg-green-500/10 border-green-500/30' 
                    : 'bg-red-500/10 border-red-500/30'
                }`}>
                  <div className="flex items-center gap-3 mb-3">
                    {testResult.passed ? (
                      <CheckCircle className="w-6 h-6 text-green-400" />
                    ) : (
                      <XCircle className="w-6 h-6 text-red-400" />
                    )}
                    <div>
                      <h4 className={`font-semibold ${testResult.passed ? 'text-green-400' : 'text-red-400'}`}>
                        {testResult.passed ? 'Content Approved' : 'Content Rejected'}
                      </h4>
                      <p className="text-sm text-gray-400">
                        Action: <Badge className={testResult.action === 'approve' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                          {testResult.action?.toUpperCase()}
                        </Badge>
                      </p>
                    </div>
                  </div>
                  
                  {testResult.flagged_categories?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm text-gray-400 mb-2">Flagged Categories:</p>
                      <div className="flex flex-wrap gap-2">
                        {testResult.flagged_categories.map((cat) => (
                          <Badge key={cat} className="bg-red-500/20 text-red-400 border border-red-500/30">
                            {getCategoryIcon(cat)} {cat.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {testResult.reason && (
                    <div className="mt-3 p-3 bg-gray-800/50 rounded-lg">
                      <p className="text-sm text-gray-400">
                        <span className="font-medium text-white">Reason: </span>
                        {testResult.reason}
                      </p>
                    </div>
                  )}

                  {testResult.confidence_scores && Object.keys(testResult.confidence_scores).length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm text-gray-400 mb-2">Confidence Scores:</p>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(testResult.confidence_scores).map(([cat, score]) => (
                          <div key={cat} className="flex items-center justify-between p-2 bg-gray-800/50 rounded">
                            <span className="text-xs text-gray-400">{cat.replace(/_/g, ' ')}</span>
                            <span className="text-xs font-mono text-yellow-400">{(score * 100).toFixed(0)}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Quick Test Examples */}
              <div className="mt-6">
                <p className="text-sm text-gray-400 mb-2">Quick Test Examples:</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: 'Clean content', text: 'Check out my new music video featuring original beats and creative visuals!' },
                    { label: 'Spam', text: 'FREE MONEY!!! Click here now to win $10000 guaranteed! Limited time offer!' },
                    { label: 'Drug content', text: 'Best place to buy drugs online, DM for prices and fast shipping worldwide' },
                    { label: 'Adult (allowed)', text: 'New explicit NSFW photoshoot available for subscribers only' }
                  ].map((example) => (
                    <Button
                      key={example.label}
                      onClick={() => setTestText(example.text)}
                      variant="outline"
                      size="sm"
                      className="border-gray-600 text-xs"
                    >
                      {example.label}
                    </Button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs">
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-400" />
                    Moderation Logs
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Recent moderation actions and decisions
                  </CardDescription>
                </div>
                <Button onClick={fetchLogs} variant="outline" size="sm" className="border-gray-600">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Filter Buttons */}
              <div className="flex gap-2 mb-4">
                {['all', 'CONTENT_APPROVED', 'CONTENT_REJECTED', 'CONTENT_FLAGGED'].map((filter) => (
                  <Button
                    key={filter}
                    onClick={() => setLogFilter(filter)}
                    variant={logFilter === filter ? 'default' : 'outline'}
                    size="sm"
                    className={logFilter === filter ? 'bg-yellow-500 text-black' : 'border-gray-600'}
                  >
                    {filter === 'all' ? 'All' : filter.replace('CONTENT_', '')}
                  </Button>
                ))}
              </div>

              {/* Logs List */}
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {logs.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No moderation logs yet</p>
                    <p className="text-sm">Logs will appear here when content is moderated</p>
                  </div>
                ) : (
                  logs
                    .filter(log => logFilter === 'all' || log.action === logFilter)
                    .map((log) => (
                      <div key={log.id || `${log.timestamp}-${log.action}`} className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {log.action === 'CONTENT_APPROVED' && <CheckCircle className="w-4 h-4 text-green-400" />}
                            {log.action === 'CONTENT_REJECTED' && <XCircle className="w-4 h-4 text-red-400" />}
                            {log.action === 'CONTENT_FLAGGED' && <AlertTriangle className="w-4 h-4 text-yellow-400" />}
                            <Badge className={
                              log.action === 'CONTENT_APPROVED' ? 'bg-green-500/20 text-green-400' :
                              log.action === 'CONTENT_REJECTED' ? 'bg-red-500/20 text-red-400' :
                              'bg-yellow-500/20 text-yellow-400'
                            }>
                              {log.action?.replace('CONTENT_', '')}
                            </Badge>
                            <span className="text-sm text-gray-400">{log.content_type || 'text'}</span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {log.timestamp ? new Date(log.timestamp).toLocaleString() : 'Unknown'}
                          </span>
                        </div>
                        
                        {log.flagged_categories?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {log.flagged_categories.map((cat) => (
                              <Badge key={cat} variant="outline" className="text-xs border-gray-600 text-gray-400">
                                {getCategoryIcon(cat)} {cat}
                              </Badge>
                            ))}
                          </div>
                        )}
                        
                        {log.reason && (
                          <p className="text-sm text-gray-400 mt-2 truncate">{log.reason}</p>
                        )}
                      </div>
                    ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-400" />
                Moderation Settings
              </CardTitle>
              <CardDescription className="text-gray-400">
                Configure how content moderation works
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
                <div>
                  <p className="text-white font-medium">Enable AI Moderation</p>
                  <p className="text-sm text-gray-400">Automatically analyze uploaded content</p>
                </div>
                <Switch
                  checked={settings?.enabled ?? true}
                  onCheckedChange={(checked) => handleToggleSetting('enabled', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
                <div>
                  <p className="text-white font-medium">Auto-Reject Flagged Content</p>
                  <p className="text-sm text-gray-400">Immediately reject content that violates policies</p>
                </div>
                <Switch
                  checked={settings?.auto_reject ?? true}
                  onCheckedChange={(checked) => handleToggleSetting('auto_reject', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
                <div>
                  <p className="text-white font-medium">Allow Adult Content</p>
                  <p className="text-sm text-gray-400">NSFW and explicit content is permitted</p>
                </div>
                <Switch
                  checked={settings?.adult_content_allowed ?? true}
                  onCheckedChange={(checked) => handleToggleSetting('adult_content_allowed', checked)}
                />
              </div>

              <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <p className="text-blue-400 text-sm">
                  <strong>Note:</strong> Changes to settings take effect immediately for new uploads. 
                  Existing content is not re-moderated.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories">
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-cyan-400" />
                Moderation Categories
              </CardTitle>
              <CardDescription className="text-gray-400">
                Categories being monitored and their violation counts (last 30 days)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {settings?.categories?.map((category) => {
                  const count = stats?.by_category?.[category.toUpperCase()] || 0;
                  const description = settings?.category_descriptions?.[category] || '';
                  
                  return (
                    <div key={category} className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{getCategoryIcon(category)}</span>
                          <span className="text-white font-medium capitalize">
                            {category.replace(/_/g, ' ')}
                          </span>
                        </div>
                        <Badge className={count > 0 ? 'bg-red-500/20 text-red-400' : 'bg-gray-600 text-gray-400'}>
                          {count} violations
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-400">{description}</p>
                    </div>
                  );
                })}
              </div>

              {/* Adult Content Notice */}
              <div className="mt-6 p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl">🔞</span>
                  <span className="text-purple-400 font-medium">Adult Content Policy</span>
                </div>
                <p className="text-sm text-gray-400">
                  Adult/NSFW content is <strong className="text-green-400">ALLOWED</strong> on this platform. 
                  Explicit material, nudity, and sexual content will not be flagged by the moderation system.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const StatCard = ({ icon: Icon, title, value, color, bgColor }) => (
  <Card className={`bg-gradient-to-r ${bgColor} border-gray-700`}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-400 text-sm">{title}</p>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
        </div>
        <Icon className={`w-8 h-8 ${color} opacity-50`} />
      </div>
    </CardContent>
  </Card>
);

export default ModerationTab;
